import {
  Box,
  BoxProps,
  Chip,
  Skeleton,
  Typography,
  TypographyProps
} from '@dtx-company/design-system/src'
import { BuyPlanPageDivider } from '../BuyPlanPageDivider/BuyPlanPageDivider'
import { PlanPurchaseFormData } from '@app/plans/src/PlanPurchaseForm/PlanPurchaseFormData'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { getPriceText } from '@app/common/src/utils/getPriceText'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useBuyPlanPageContext } from '../../context/useBuyPlanPageContext'
import { useFormContext } from 'react-hook-form'
import { usePlanCostSummarySWR } from '@app/common/src/hooks/usePlanCostSummarySWR'
import { usePlanPurchaseProviderContext } from '@app/plans/src/purchase/usePlanPurchaseProviderContext/usePlanPurchaseProviderContext'

function LineItem({
  shade,
  label,
  chipText,
  priceInCents,
  sx,
  onDeleteChip,
  includeZero
}: {
  shade?: 'light' | 'dark'
  label: string
  chipText?: string
  priceInCents: number | undefined
  sx?: BoxProps['sx']
  onDeleteChip?: () => void
  includeZero?: boolean
}): JSX.Element | null {
  if (priceInCents === undefined) return null
  if (priceInCents === 0 && !includeZero) return null
  const priceText = getPriceText({ priceInCents })
  const sxArray = Array.isArray(sx) ? sx : [sx]
  const labelColor: TypographyProps['color'] = shade === 'dark' ? '#242424' : '#696763'
  const priceColor: TypographyProps['color'] = priceInCents < 0 ? '#005F2D' : labelColor
  return (
    <Box sx={sxArray.concat({ display: 'flex', flexFlow: 'row nowrap', alignItems: 'center' })}>
      <Box sx={{ flex: '1 1 auto', paddingRight: '32px' }}>
        <Typography component="span" variant="subtitleSmall" color={labelColor}>
          {label}{' '}
          {chipText && (
            <Chip
              variant="filled"
              size="small"
              label={chipText.toLocaleUpperCase()}
              onDelete={onDeleteChip}
            />
          )}
        </Typography>
      </Box>
      <Box>
        <Typography component="span" variant="subtitleSmall" color={priceColor}>
          {priceText}
        </Typography>
      </Box>
    </Box>
  )
}

export function BuyPlanPagePlanSummary(): JSX.Element {
  const { document, planExtraDetails } = useBuyPlanPageContext()
  const { data } = document
  const authState = useAuthState()
  const { selectedPlan } = usePlanPurchaseProviderContext()
  const { setValue, watch } = useFormContext<PlanPurchaseFormData>()
  const promoCode = watch('promoCode')
  const planCostSummarySWR = usePlanCostSummarySWR(
    () => {
      if (!authState.token) return
      if (!selectedPlan) return
      // NOTE: no longer passes payment method or address so that we
      // don't calculate tax anymore
      return {
        token: authState.token,
        newPlanId: selectedPlan.id,
        promoCode,
        planExtraDetails
      }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true
    }
  )

  const summary = planCostSummarySWR.data

  if (!summary) {
    return (
      <>
        <Skeleton sx={{ height: '24px' }} />
        <Skeleton sx={{ height: '24px' }} />
      </>
    )
  }

  const hasAdvancedPricingDetails = summary.basePriceInCents !== summary.grandTotalInCents

  return (
    <Box>
      {hasAdvancedPricingDetails && (
        <>
          {summary.additionalPlanExtras?.map(extra => {
            return (
              <LineItem
                key={extra.planExtraId}
                label={`${
                  extra.planExtraType === 'seats'
                    ? data.additional_seats_label
                    : 'Additional extras'
                } ${extra.quantity}`}
                priceInCents={extra.priceInCents}
              />
            )
          })}
          <LineItem
            label={data.base_price_label || ''}
            priceInCents={summary.basePriceInCents}
            sx={{ mt: '8px' }}
          />
          <LineItem
            label={data.annual_discount_label || ''}
            priceInCents={summary.annualDiscountInCents}
            sx={{ mt: '8px' }}
          />
          <LineItem
            label={data.promo_code_label || ''}
            priceInCents={summary.couponDiscountInCents}
            chipText={promoCode}
            onDeleteChip={() => {
              fireAnalyticsEvent('BuyPlanPage_PromoCodeRemoveButton_Clicked', {
                promoCode
              })
              setValue('promoCode', undefined)
            }}
            sx={{ mt: '8px' }}
          />
          <LineItem
            label={data.proration_label || ''}
            priceInCents={summary.prorationAppliedInCents}
            sx={{ mt: '8px' }}
          />
          <LineItem
            label={data.credits_label || ''}
            priceInCents={summary.creditAppliedInCents}
            sx={{ mt: '8px' }}
          />
          <BuyPlanPageDivider sx={{ my: 4 }} />
        </>
      )}
      <LineItem
        shade="dark"
        label={data.grand_total_label || ''}
        priceInCents={summary.grandTotalInCents}
        sx={{ fontWeight: 'bold' }}
        includeZero
      />
      {data.sales_tax_text && (
        <Box sx={{ textAlign: 'right', mt: '8px', color: '#696763' }}>
          <Typography variant="labelMedium">{data.sales_tax_text}</Typography>
        </Box>
      )}
    </Box>
  )
}
